import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import { MatrixFlowDiagram } from "@/components/site/MatrixFlowDiagram";
import HudCorners from "@/components/site/_partials/HudCorners";
import { FadeUp } from "@/components/site/_shared";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { INTENT_PAGES } from "@/data/intentPages";
import {
  organizationSchema,
  medicalWebPageSchema,
  wellnessWebPageSchema,
  breadcrumbSchema,
  faqSchema,
  intentOgImage,
  BRAND, SITE_URL} from "@/lib/seo";

const LAST_REVIEWED = "June 2026";

function QASection({ section, index }) {
  return (
    <FadeUp as="section" className="border-l-2 border-accent/40 pl-6 md:pl-8">
      <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-muted-foreground mb-2">
        Q.{String(index + 1).padStart(2, "0")}
      </div>
      <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-primary leading-snug">
        {section.q}
      </h2>
      <p className="mt-4 text-base md:text-lg text-foreground/85 leading-relaxed font-medium">
        {section.a}
      </p>
      {section.detail && (
        <p className="mt-3 text-base text-foreground/70 leading-relaxed">{section.detail}</p>
      )}
    </FadeUp>
  );
}

export default function IntentPage({ slug }) {
  const page = INTENT_PAGES[slug];
  const url = `${SITE_URL}/${page.slug}`;

  useDocumentMeta({
    title: page.metaTitle,
    description: page.metaDescription,
    image: intentOgImage(page.slug),
    url,
    schema: [
      organizationSchema(),
      page.isMedical
        ? medicalWebPageSchema({ name: page.metaTitle, about: page.about || page.h1 })
        : wellnessWebPageSchema({ name: page.metaTitle }),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: page.h1, url: `/${page.slug}` },
      ]),
      faqSchema(page.faqs),
    ],
  });

  return (
    <div data-testid={`intent-page-${page.slug}`} className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />

      <main className="pt-28 md:pt-32 pb-20 md:pb-28">
        {/* Hero */}
        <section className="relative max-w-6xl mx-auto px-4 md:px-8">
          <div className="absolute inset-0 iw-grid-bg opacity-30 -z-10" aria-hidden="true" />
          <div
            className={
              page.image
                ? "grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center"
                : "max-w-4xl mx-auto"
            }
          >
            <div className={page.image ? "lg:col-span-7" : ""}>
              <nav aria-label="Breadcrumb" data-testid="intent-breadcrumb" className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-muted-foreground">
                <a href="/" className="hover:text-accent transition-colors">Home</a>
                <span aria-hidden className="mx-2 text-accent">/</span>
                <span className="text-primary">{page.eyebrow}</span>
              </nav>

              <div className="mt-8 iw-overline">{page.eyebrow}</div>
              <h1 className="mt-4 font-display font-bold tracking-tight leading-[1.05] text-[clamp(2rem,4.8vw,3.5rem)] text-primary">
                {page.h1}
              </h1>
              <p
                data-testid="intent-direct-answer"
                className="mt-6 text-lg md:text-xl text-foreground/85 leading-relaxed font-medium"
              >
                {page.directAnswer}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="/get-started"
                  data-testid="intent-hero-cta"
                  className="iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]"
                >
                  Start My Health Assessment <span aria-hidden>→</span>
                </a>
                <Link
                  to="/agents/rhea"
                  data-testid="intent-ask-rhea"
                  className="iw-btn-ghost inline-flex items-center gap-2 px-6 py-3.5 text-[0.7rem]"
                >
                  Ask Rhea
                </Link>
              </div>
            </div>

            {page.image && (
              <FadeUp className="lg:col-span-5">
                <div
                  data-testid="intent-hero-image"
                  className="iw-scan-sweep relative aspect-[4/5] overflow-hidden border border-border bg-foreground shadow-md"
                >
                  <img
                    src={page.image}
                    alt={page.imageAlt || page.h1}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#11296B]/25 to-transparent pointer-events-none" />
                  <HudCorners offset={10} size={10} />
                </div>
              </FadeUp>
            )}
          </div>
        </section>

        {/* Q&A sections */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 mt-20 md:mt-24 space-y-14 md:space-y-16">
          {page.sections.map((s, i) => (
            <QASection key={s.q} section={s} index={i} />
          ))}
        </section>

        {/* What we measure */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 mt-20 md:mt-24">
          <FadeUp>
            <div className="iw-overline">What We Measure</div>
            <div data-testid="intent-measures" className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {page.measures.map((m) => (
                <div
                  key={m}
                  className="border border-border bg-card py-3 px-3 text-center font-mono text-[0.62rem] md:text-[0.68rem] uppercase tracking-[0.1em] text-primary font-semibold"
                >
                  {m}
                </div>
              ))}
            </div>
          </FadeUp>
        </section>

        {/* Canonical pipeline diagram */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 mt-20 md:mt-24">
          <FadeUp>
            <div className="iw-overline mb-6">How Your Data Becomes a Protocol</div>
            <MatrixFlowDiagram compact />
          </FadeUp>
        </section>

        {/* Clinical review strip */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 mt-16">
          <div
            data-testid="intent-reviewer-strip"
            className="border border-border bg-card p-6 md:p-7 font-mono text-[0.7rem] md:text-[0.75rem] leading-relaxed tracking-wide text-foreground/70 space-y-1.5"
          >
            <p>
              <span className="text-accent font-semibold">Medically reviewed by:</span>{" "}
              <strong className="text-primary">{BRAND.medicalDirector.name}</strong> — Medical Director, {BRAND.medicalOrg}
            </p>
            <p>
              <span className="text-accent font-semibold">Scientific/technical contribution:</span>{" "}
              <strong className="text-primary">{BRAND.founder.name}</strong> — Founder &amp; CEO
            </p>
            <p>
              <span className="text-accent font-semibold">Last reviewed:</span> {LAST_REVIEWED}
            </p>
          </div>
        </section>

        {/* FAQ — native <details> so answers exist in prerendered HTML */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 mt-20 md:mt-24">
          <FadeUp>
            <div className="iw-overline">Frequently Asked Questions</div>
            <div data-testid="intent-faq" className="mt-6 divide-y divide-border border border-border bg-card">
              {page.faqs.map((f) => (
                <details key={f.q} className="group px-6 md:px-7">
                  <summary className="cursor-pointer list-none py-5 flex items-start justify-between gap-4">
                    <h3 className="font-display text-base md:text-lg font-semibold text-primary leading-snug m-0">
                      {f.q}
                    </h3>
                    <span aria-hidden className="text-accent font-bold text-lg leading-none mt-0.5 group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <p className="pb-6 text-base text-foreground/75 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </FadeUp>
        </section>

        {/* Related + CTA */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 mt-20 md:mt-24">
          <FadeUp>
            <div className="iw-overline">Related</div>
            <div data-testid="intent-related" className="mt-5 flex flex-wrap gap-3">
              {page.related.map((r) => (
                <a
                  key={r.href}
                  href={r.href}
                  className="iw-btn-ghost inline-flex items-center gap-2 px-5 py-2.5 text-[0.62rem]"
                >
                  {r.label} <span aria-hidden>→</span>
                </a>
              ))}
            </div>

            <div className="mt-14 border-2 border-primary/25 bg-card p-8 md:p-10 text-center">
              <div className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">
                Ready to see your own data?
              </div>
              <p className="mt-3 text-base md:text-lg text-foreground/70 max-w-xl mx-auto">
                A free, guided assessment — routed to the right clinical or wellness pathway.
              </p>
              <a
                href="/get-started"
                data-testid="intent-bottom-cta"
                className="mt-7 iw-btn-red inline-flex items-center gap-2 px-8 py-4 text-[0.72rem]"
              >
                Start My Health Assessment <span aria-hidden>→</span>
              </a>
            </div>
          </FadeUp>
        </section>
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
